import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { env } from '@/config/env';
import type {
  Explosion,
  Trajectory,
  TrajectoryUpdatePayload,
  MovingTrack,
  TrackUpdatePayload,
  SimParams,
  ExplosionClassName,
  SimResult,
  RansacBaseSolver,
  LocalizationQuality,
  MonitorQuadrant,
  QuadrantAlert,
} from '@/types/geolocation';
import { validateClusterUpdate } from '@/utils/validatePayload';
import { haversineMeters, type LatLon } from './geo';

const MAX_EXPLOSIONS = 200;
const MAX_TRAJECTORIES = 200;
const MAX_TRACKS = 100;

/** Live-radar radius: events farther than this from the user are hidden in the user app. */
export const NEARBY_RADIUS_M = 20000;

/** An explosion annotated with its distance (metres) from the user — for the live radar feed. */
export type NearbyExplosion = Explosion & { distanceM: number };

/** A drone trajectory annotated with its closest distance (metres) to the user — radar feed/map. */
export type NearbyTrajectory = Trajectory & { distanceM: number };

/** A live moving-object track annotated with its current distance (metres) to the user. */
export type NearbyTrack = MovingTrack & { distanceM: number };

/** Map viewport bbox as the backend expects it: [minLat, minLon, maxLat, maxLon]. */
export type RegionBbox = [number, number, number, number];

export const useGeolocationStore = defineStore('geolocation', () => {
  const explosions = ref<Explosion[]>([]);
  const trajectories = ref<Trajectory[]>([]);
  const tracks = ref<MovingTrack[]>([]);
  const selectedExplosionId = ref<string | null>(null);
  const hoveredExplosionId = ref<string | null>(null);
  const connected = ref(false);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const simResult = ref<SimResult | null>(null);

  const filterClassName = ref<ExplosionClassName | 'all'>('all');
  const filterMaxGdop = ref<number>(Infinity);
  const filterTimeRange = ref<{ from: number; to: number } | null>(null);

  const lastDisconnectTime = ref<number | null>(null);
  const isStaleData = ref(false);

  /** User's own position — the centre of the live-radar circle (set from device GPS). */
  const userCenter = ref<LatLon | null>(null);

  /** How many *other* people are monitoring within the user's radius right now (server-pushed). */
  const monitorsNearby = ref(0);

  /** Privacy-preserving monitor density grid: quadrants with counts near the user. */
  const monitorQuadrants = ref<MonitorQuadrant[]>([]);

  /** Explosion heatmap: quadrants highlighted red, auto-expire after 30 s. */
  const explosionQuadrants = ref<Map<string, QuadrantAlert & { expiresAt: number }>>(new Map());

  const selectedExplosion = computed(() =>
    explosions.value.find((e) => e.id === selectedExplosionId.value) ?? null,
  );

  const hoveredExplosion = computed(() =>
    explosions.value.find((e) => e.id === hoveredExplosionId.value) ?? null,
  );

  const filteredExplosions = computed(() => {
    let result = explosions.value;

    if (filterClassName.value !== 'all') {
      result = result.filter((e) => e.className === filterClassName.value);
    }

    if (filterMaxGdop.value < Infinity) {
      result = result.filter((e) => e.gdop <= filterMaxGdop.value);
    }

    if (filterTimeRange.value) {
      result = result.filter(
        (e) =>
          e.createdAt >= filterTimeRange.value!.from &&
          e.createdAt <= filterTimeRange.value!.to,
      );
    }

    return result;
  });

  const filteredTrajectories = computed(() => {
    let result = trajectories.value;
    if (filterClassName.value !== 'all') {
      result = result.filter((t) => t.className === filterClassName.value);
    }
    if (filterTimeRange.value) {
      result = result.filter(
        (t) =>
          t.createdAt >= filterTimeRange.value!.from &&
          t.createdAt <= filterTimeRange.value!.to,
      );
    }
    return result;
  });

  const filteredTracks = computed(() => {
    let result = tracks.value;
    if (filterClassName.value !== 'all') {
      result = result.filter((t) => t.className === filterClassName.value);
    }
    return result;
  });

  /**
   * Live-radar feed: explosions within NEARBY_RADIUS_M of the user, annotated with their
   * distance and sorted nearest-first. Empty until `userCenter` is known (GPS / cached centre).
   */
  const nearbyExplosions = computed<NearbyExplosion[]>(() => {
    const c = userCenter.value;
    if (!c) return [];
    return filteredExplosions.value
      .map((e) => ({ ...e, distanceM: haversineMeters(c, { lat: e.latitude, lon: e.longitude }) }))
      .filter((e) => e.distanceM <= NEARBY_RADIUS_M)
      .sort((a, b) => a.distanceM - b.distanceM);
  });

  const nearbyCount = computed(() => nearbyExplosions.value.length);

  /**
   * Live-radar feed/map: drone trajectories whose path passes within NEARBY_RADIUS_M of the user.
   * Distance is the closest approach of the reconstructed path (curve preferred) to the user, so a
   * fly-by that crosses overhead ranks nearest even if its origin is far. Sorted nearest-first.
   */
  const nearbyTrajectories = computed<NearbyTrajectory[]>(() => {
    const c = userCenter.value;
    if (!c) return [];
    return filteredTrajectories.value
      .map((t) => {
        const path = (t.curvePolyline?.length ? t.curvePolyline : t.polyline).length
          ? (t.curvePolyline?.length ? t.curvePolyline : t.polyline)
          : [{ lat: t.originLat, lon: t.originLon }];
        const distanceM = Math.min(...path.map((p) => haversineMeters(c, { lat: p.lat, lon: p.lon })));
        return { ...t, distanceM };
      })
      .filter((t) => t.distanceM <= NEARBY_RADIUS_M)
      .sort((a, b) => a.distanceM - b.distanceM);
  });

  /** Live-radar: continuously-tracked moving objects whose current position is within range. */
  const nearbyTracks = computed<NearbyTrack[]>(() => {
    const c = userCenter.value;
    if (!c) return [];
    return filteredTracks.value
      .map((t) => ({ ...t, distanceM: haversineMeters(c, { lat: t.lat, lon: t.lon }) }))
      .filter((t) => t.distanceM <= NEARBY_RADIUS_M)
      .sort((a, b) => a.distanceM - b.distanceM);
  });

  const explosionCount = computed(() => explosions.value.length);
  const filteredCount = computed(() => filteredExplosions.value.length);
  const trajectoryCount = computed(() => trajectories.value.length);
  const trackCount = computed(() => tracks.value.length);

  const staleDuration = computed((): string => {
    if (!lastDisconnectTime.value) return '';
    const ms = Date.now() - lastDisconnectTime.value;
    if (ms < 60000) return `${Math.round(ms / 1000)}s`;
    if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
    return `${Math.round(ms / 3600000)}h`;
  });

  function parseExplosion(raw: any): Explosion {
    const rmsResidual = raw.rmsResidual ?? 0;
    return {
      id: raw._id ?? raw.id,
      latitude: raw.latitude,
      longitude: raw.longitude,
      gdop: raw.gdop,
      confidence: raw.confidence,
      converged: raw.converged,
      method: raw.method,
      sensorCount: raw.sensorCount,
      className: raw.className,
      rmsResidual,
      uncertaintyRadiusM: Math.max(rmsResidual, 1),
      quality: raw.quality,
      createdAt: new Date(raw.createdAt).getTime(),
      sensorPositions: raw.sensorPositions ?? [],
      perSensor: raw.perSensor ?? [],
    };
  }

  function parseTrajectory(raw: any): Trajectory {
    return {
      id: raw._id ?? raw.id,
      className: raw.className,
      trackId: raw.trackId ?? null,
      originLat: raw.originLat,
      originLon: raw.originLon,
      headingDeg: raw.headingDeg ?? 0,
      speedMps: raw.speedMps ?? 0,
      polyline: Array.isArray(raw.polyline) ? raw.polyline : [],
      curvePolyline: Array.isArray(raw.curvePolyline) ? raw.curvePolyline : undefined,
      cpaPoints: Array.isArray(raw.cpaPoints) ? raw.cpaPoints : undefined,
      cost: raw.cost ?? 0,
      converged: raw.converged ?? false,
      quality: (raw.quality ?? 'provisional') as LocalizationQuality,
      posSigmaM: raw.posSigmaM ?? 0,
      headingSigmaDeg: raw.headingSigmaDeg ?? 0,
      sensorCount: raw.sensorCount ?? 0,
      createdAt: new Date(raw.createdAt).getTime(),
      sensorPositions: raw.sensorPositions ?? [],
    };
  }

  /**
   * Insert or replace a trajectory keyed by its MovingTrack: a new pass of the same fly-by
   * replaces the cluster's previous snapshot rather than stacking another arrow. Falls back
   * to the document id when no trackId is present (untracked / legacy snapshots).
   */
  function upsertTrajectory(traj: Trajectory): void {
    const sameTrack = traj.trackId
      ? trajectories.value.findIndex((t) => t.trackId === traj.trackId)
      : -1;
    const idx = sameTrack >= 0 ? sameTrack : trajectories.value.findIndex((t) => t.id === traj.id);
    if (idx >= 0) {
      trajectories.value[idx] = traj;
    } else {
      trajectories.value.unshift(traj);
      if (trajectories.value.length > MAX_TRAJECTORIES) {
        trajectories.value = trajectories.value.slice(0, MAX_TRAJECTORIES);
      }
    }
  }

  /** Collapse a list to one trajectory per MovingTrack, keeping the most recent. */
  function dedupeTrajectories(list: Trajectory[]): Trajectory[] {
    const byTrack = new Map<string, Trajectory>();
    const untracked: Trajectory[] = [];
    for (const t of list) {
      if (!t.trackId) {
        untracked.push(t);
        continue;
      }
      const prev = byTrack.get(t.trackId);
      if (!prev || t.createdAt > prev.createdAt) byTrack.set(t.trackId, t);
    }
    return [...byTrack.values(), ...untracked].sort((a, b) => b.createdAt - a.createdAt);
  }

  /** Build the bbox query string the backend expects: minLat,minLon,maxLat,maxLon. */
  function bboxParam(bbox?: RegionBbox): string {
    return bbox ? `&bbox=${bbox.join(',')}` : '';
  }

  async function fetchExplosions(limit: number = 100): Promise<void> {
    try {
      loading.value = true;
      error.value = null;
      const res = await fetch(`${env.apiUrl}/explosions?limit=${limit}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      explosions.value = data.map(parseExplosion);
    } catch (e: any) {
      error.value = `Fetch error: ${e.message}`;
    } finally {
      loading.value = false;
    }
  }

  async function fetchTrajectories(limit: number = 100): Promise<void> {
    try {
      const res = await fetch(`${env.apiUrl}/trajectories?limit=${limit}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      trajectories.value = dedupeTrajectories(data.map(parseTrajectory));
    } catch (e) {
      error.value = `Fetch trajectories error: ${(e as Error).message}`;
    }
  }

  /**
   * "Pull by region" downlink: fetch both explosions and trajectories that fall inside
   * the current map viewport (and optionally after `since`). Replaces the in-memory set
   * with the region result — used on map open and on pan/zoom.
   */
  async function fetchByRegion(bbox: RegionBbox, since?: number, limit = 200): Promise<void> {
    const sinceParam = since ? `&since=${since}` : '';
    try {
      loading.value = true;
      error.value = null;
      const [expRes, trajRes] = await Promise.all([
        fetch(`${env.apiUrl}/explosions?limit=${limit}${bboxParam(bbox)}${sinceParam}`),
        fetch(`${env.apiUrl}/trajectories?limit=${limit}${bboxParam(bbox)}${sinceParam}`),
      ]);
      if (expRes.ok) {
        explosions.value = (await expRes.json()).map(parseExplosion);
      }
      if (trajRes.ok) {
        trajectories.value = dedupeTrajectories((await trajRes.json()).map(parseTrajectory));
      }
    } catch (e) {
      error.value = `Region fetch error: ${(e as Error).message}`;
    } finally {
      loading.value = false;
    }
  }

  async function simulateExplosion(params: SimParams): Promise<SimResult | null> {
    loading.value = true;
    error.value = null;
    simResult.value = null;
    try {
      const res = await fetch(`${env.apiUrl}/events/simulate-explosion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt}`);
      }
      const data: SimResult = await res.json();
      simResult.value = data;
      await fetchExplosions();
      return data;
    } catch (e: any) {
      error.value = `Simulate error: ${e.message}`;
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function resimulateTdoa(
    groundTruth: { lat: number; lon: number },
    sensors: Array<{ lat: number; lon: number; onsetTimeMs: number }>,
    solver: 'gauss-newton' | 'chan-ho' | 'bancroft' | 'ransac' | 'grid-search',
    ransacBaseSolver?: RansacBaseSolver,
    residualThresholdM?: number,
  ): Promise<{
    tdoa: { lat: number; lon: number; gdop: number; converged: boolean; nIterations: number; method: string; gdopPassed: boolean } | null;
    errorMeters: number | null;
    sensors: Array<{ lat: number; lon: number; onsetTimeMs: number; residualM: number; isOutlier: boolean }>;
    residualThresholdM: number;
  } | null> {
    try {
      const res = await fetch(`${env.apiUrl}/events/resimulate-tdoa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ groundTruth, sensors, solver, ransacBaseSolver, residualThresholdM }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt}`);
      }
      return res.json();
    } catch (e: any) {
      error.value = `Resimulate error: ${e.message}`;
      return null;
    }
  }

  function handleClusterUpdate(raw: unknown): void {
    const payload = validateClusterUpdate(raw);
    if (!payload) {
      console.warn('[GeolocationStore] Invalid cluster-update payload:', raw);
      return;
    }
    const exp = parseExplosion(payload);
    const existingIdx = explosions.value.findIndex((e) => e.id === exp.id);
    if (existingIdx >= 0) {
      explosions.value[existingIdx] = exp;
    } else {
      explosions.value.unshift(exp);
      if (explosions.value.length > MAX_EXPLOSIONS) {
        explosions.value = explosions.value.slice(0, MAX_EXPLOSIONS);
      }
    }
  }

  function handleTrajectoryUpdate(raw: TrajectoryUpdatePayload | unknown): void {
    const p = raw as TrajectoryUpdatePayload;
    if (!p || typeof p.originLat !== 'number' || typeof p.originLon !== 'number') {
      console.warn('[GeolocationStore] Invalid trajectory-update payload:', raw);
      return;
    }
    upsertTrajectory(parseTrajectory(p));
  }

  function parseTrack(raw: TrackUpdatePayload): MovingTrack {
    return {
      trackId: raw.trackId,
      className: raw.className,
      status: raw.status,
      lat: raw.lat,
      lon: raw.lon,
      headingDeg: raw.headingDeg ?? 0,
      speedMps: raw.speedMps ?? 0,
      posSigmaM: raw.posSigmaM ?? 0,
      hits: raw.hits ?? 0,
      score: raw.score ?? 0,
      maneuverProb: raw.maneuverProb ?? 0,
      history: Array.isArray(raw.history) ? raw.history : [],
      predicted: Array.isArray(raw.predicted) ? raw.predicted : [],
      lastUpdateMs: raw.lastUpdateMs ?? Date.now(),
    };
  }

  /**
   * Continuous-tracking downlink. Upserts a track by its stable `trackId` (one moving
   * object → one entry that evolves over time), and prunes terminated tracks so the map
   * doesn't keep ghosts of objects that have left the area.
   */
  function handleTrackUpdate(raw: TrackUpdatePayload | unknown): void {
    const p = raw as TrackUpdatePayload;
    if (!p || typeof p.trackId !== 'string' || typeof p.lat !== 'number' || typeof p.lon !== 'number') {
      console.warn('[GeolocationStore] Invalid track-update payload:', raw);
      return;
    }
    const track = parseTrack(p);
    const idx = tracks.value.findIndex((t) => t.trackId === track.trackId);

    if (track.status === 'terminated') {
      if (idx >= 0) tracks.value.splice(idx, 1);
      return;
    }

    if (idx >= 0) {
      tracks.value[idx] = track;
    } else {
      tracks.value.unshift(track);
      if (tracks.value.length > MAX_TRACKS) {
        tracks.value = tracks.value.slice(0, MAX_TRACKS);
      }
    }
  }

  /** Hydrate the store from a local (IndexedDB) cache before the network round-trip. */
  function hydrateFromCache(cachedExplosions: Explosion[], cachedTrajectories: Trajectory[]): void {
    if (cachedExplosions.length && explosions.value.length === 0) {
      explosions.value = cachedExplosions.slice(0, MAX_EXPLOSIONS);
    }
    if (cachedTrajectories.length && trajectories.value.length === 0) {
      trajectories.value = cachedTrajectories.slice(0, MAX_TRAJECTORIES);
    }
  }

  function selectExplosion(id: string | null): void {
    selectedExplosionId.value = id;
  }

  function hoverExplosion(id: string | null): void {
    hoveredExplosionId.value = id;
  }

  function setFilterClassName(className: ExplosionClassName | 'all'): void {
    filterClassName.value = className;
  }

  function setFilterMaxGdop(maxGdop: number): void {
    filterMaxGdop.value = maxGdop;
  }

  function setFilterTimeRange(range: { from: number; to: number } | null): void {
    filterTimeRange.value = range;
  }

  function clearFilters(): void {
    filterClassName.value = 'all';
    filterMaxGdop.value = Infinity;
    filterTimeRange.value = null;
  }

  function clearError(): void {
    error.value = null;
  }

  function setConnected(value: boolean): void {
    connected.value = value;
  }

  function setUserCenter(center: LatLon | null): void {
    userCenter.value = center;
  }

  function setMonitorsNearby(n: number): void {
    monitorsNearby.value = Number.isFinite(n) && n > 0 ? n : 0;
  }

  function setMonitorQuadrants(quads: MonitorQuadrant[]): void {
    monitorQuadrants.value = quads;
  }

  function addExplosionQuadrant(alert: QuadrantAlert): void {
    const key = `${alert.row},${alert.col}`;
    const expiresAt = Date.now() + 30_000;
    const updated = new Map(explosionQuadrants.value);
    updated.set(key, { ...alert, expiresAt });
    explosionQuadrants.value = updated;
  }

  function removeExplosionQuadrant(row: number, col: number): void {
    const key = `${row},${col}`;
    const updated = new Map(explosionQuadrants.value);
    updated.delete(key);
    explosionQuadrants.value = updated;
  }

  function markDisconnected(): void {
    lastDisconnectTime.value = Date.now();
    isStaleData.value = true;
  }

  function markSynced(): void {
    isStaleData.value = false;
  }

  async function syncGap(): Promise<void> {
    if (!lastDisconnectTime.value) {
      await Promise.all([fetchExplosions(100), fetchTrajectories(100)]);
      return;
    }

    const gapStart = lastDisconnectTime.value;

    try {
      loading.value = true;
      const [expRes, trajRes] = await Promise.all([
        fetch(`${env.apiUrl}/explosions?since=${gapStart}&limit=200`),
        fetch(`${env.apiUrl}/trajectories?since=${gapStart}&limit=200`),
      ]);
      if (!expRes.ok) throw new Error(`HTTP ${expRes.status}`);

      const expData: any[] = await expRes.json();
      const existingExp = new Set(explosions.value.map((e) => e.id));
      const newExp = expData.map(parseExplosion).filter((exp) => !existingExp.has(exp.id));
      explosions.value = [...newExp, ...explosions.value].slice(0, MAX_EXPLOSIONS);

      if (trajRes.ok) {
        const trajData: any[] = await trajRes.json();
        const existingTraj = new Set(trajectories.value.map((t) => t.id));
        const newTraj = trajData.map(parseTrajectory).filter((t) => !existingTraj.has(t.id));
        trajectories.value = dedupeTrajectories(
          [...newTraj, ...trajectories.value].slice(0, MAX_TRAJECTORIES),
        );
      }

      markSynced();
    } catch (e: any) {
      error.value = `Gap sync failed: ${e.message}`;
    } finally {
      loading.value = false;
    }
  }

  return {
    explosions,
    trajectories,
    tracks,
    selectedExplosionId,
    hoveredExplosionId,
    connected,
    loading,
    error,
    simResult,
    filterClassName,
    filterMaxGdop,
    filterTimeRange,
    lastDisconnectTime,
    isStaleData,
    userCenter,
    monitorsNearby,
    monitorQuadrants,
    explosionQuadrants,

    selectedExplosion,
    hoveredExplosion,
    filteredExplosions,
    filteredTrajectories,
    filteredTracks,
    nearbyExplosions,
    nearbyCount,
    nearbyTrajectories,
    nearbyTracks,
    explosionCount,
    filteredCount,
    trajectoryCount,
    trackCount,
    staleDuration,

    fetchExplosions,
    fetchTrajectories,
    fetchByRegion,
    simulateExplosion,
    resimulateTdoa,
    handleClusterUpdate,
    handleTrajectoryUpdate,
    handleTrackUpdate,
    hydrateFromCache,
    selectExplosion,
    hoverExplosion,
    setFilterClassName,
    setFilterMaxGdop,
    setFilterTimeRange,
    clearFilters,
    clearError,
    setConnected,
    setUserCenter,
    setMonitorsNearby,
    setMonitorQuadrants,
    addExplosionQuadrant,
    removeExplosionQuadrant,
    markDisconnected,
    markSynced,
    syncGap,
  };
});
